import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Users, Briefcase, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

interface Stats {
  totalCompanies: number;
  verifiedCompanies: number;
  totalProfessionals: number;
  diasporaProfessionals: number;
  openOpportunities: number;
  spaLinkedOpportunities: number;
}

interface SpaOpportunity {
  sector: string;
  province: string | null;
}

export function OverviewTab() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<Stats | null>(null);
  const [spaOpps, setSpaOpps] = useState<SpaOpportunity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [
        { count: totalCompanies },
        { count: verifiedCompanies },
        { count: totalProfessionals },
        { count: diasporaProfessionals },
        { count: openOpportunities },
        { count: spaLinkedOpportunities },
        { data: spaData },
      ] = await Promise.all([
        supabase.from('companies').select('*', { count: 'exact', head: true }),
        supabase.from('companies').select('*', { count: 'exact', head: true }).eq('verified', true),
        supabase.from('professionals').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('is_diaspora', true),
        supabase.from('opportunities').select('*', { count: 'exact', head: true }).eq('status', 'open'),
        supabase.from('opportunities').select('*', { count: 'exact', head: true }).eq('status', 'open').eq('spa_linked', true),
        supabase.from('opportunities').select('sector, province').eq('status', 'open').eq('spa_linked', true),
      ]);

      setStats({
        totalCompanies: totalCompanies ?? 0,
        verifiedCompanies: verifiedCompanies ?? 0,
        totalProfessionals: totalProfessionals ?? 0,
        diasporaProfessionals: diasporaProfessionals ?? 0,
        openOpportunities: openOpportunities ?? 0,
        spaLinkedOpportunities: spaLinkedOpportunities ?? 0,
      });
      setSpaOpps((spaData as SpaOpportunity[]) || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Sector chart data
  const sectorCounts: Record<string, number> = {};
  spaOpps.forEach(o => {
    sectorCounts[o.sector] = (sectorCounts[o.sector] || 0) + 1;
  });
  const sectorData = Object.entries(sectorCounts).map(([name, count]) => ({ name: t(`sectors.${name}`), count }));

  // Province chart data - top 10
  const provinceCounts: Record<string, number> = {};
  spaOpps.forEach(o => {
    if (o.province) provinceCounts[o.province] = (provinceCounts[o.province] || 0) + 1;
  });
  const provinceData = Object.entries(provinceCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({ name, count }));
  const hasProvinceData = provinceData.length > 0;

  const statCards = [
    {
      icon: Building2,
      label: t('companies.title', 'Companies'),
      primary: stats.totalCompanies,
      secondary: `${stats.verifiedCompanies} verified`,
    },
    {
      icon: Users,
      label: t('admin.tabs.diaspora', 'Professionals'),
      primary: stats.totalProfessionals,
      secondary: `${stats.diasporaProfessionals} diaspora`,
    },
    {
      icon: Briefcase,
      label: 'Open Opportunities',
      primary: stats.openOpportunities,
      secondary: `${stats.spaLinkedOpportunities} SPA-linked`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {statCards.map((s) => (
          <Card key={s.label}>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-lg bg-primary/10 p-3">
                <s.icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{s.label}</p>
                <p className="text-3xl font-bold">{s.primary}</p>
                <p className="text-sm text-muted-foreground">{s.secondary}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">SPA Opportunities by Sector</CardTitle>
          </CardHeader>
          <CardContent>
            {sectorData.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">No sector data available</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sectorData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="count" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">SPA Opportunities by Province</CardTitle>
          </CardHeader>
          <CardContent>
            {!hasProvinceData ? (
              <p className="py-8 text-center text-muted-foreground">{t('admin.noProvinceData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={provinceData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(142, 71%, 45%)" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="count" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
